import { useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';

import { useMediaQuery } from './use-media-query';

/**
 * Shared primitive for View Transitions that aren't driven by a router
 * navigation -- tab switches, filter/section changes, sheet open/close.
 * Router-driven transitions (`<Link viewTransition>` / `navigate({
 * viewTransition })`) are the easy case: TanStack Router feature-detects
 * `document.startViewTransition` itself and degrades automatically, so
 * they don't need this hook (see `create-tenant-app.tsx`'s
 * `defaultViewTransition` for that half of Section 08's item).
 *
 * This hook exists for the other half: a `setState` call has no built-in
 * snapshot timing the way a route change does. React 18 may not have
 * committed the DOM by the time `startViewTransition` snapshots it, so the
 * animation silently no-ops or captures a stale frame -- the fix is
 * `document.startViewTransition(() => flushSync(() => setState(next)))`,
 * wrapped once here rather than sprinkled through every view that wants a
 * transition.
 *
 * Not wired into any view yet in this pass -- this is the tested primitive
 * topic 08's checklist asks for; per-view adoption (tab switches, etc.) is
 * follow-up work, same scoping as the shared-element cover-morph
 * generalization.
 */
export function useViewTransition() {
	const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
	const activeTransitionRef = useRef<ViewTransition | null>(null);

	return useCallback(
		(update: () => void) => {
			if (
				prefersReducedMotion ||
				typeof document.startViewTransition !== 'function'
			) {
				update();
				return;
			}

			// iOS Safari's edge-swipe back and fast repeated taps can start a
			// new transition while one is still running -- skip the running one
			// rather than let them stack. `skipTransition()` is a no-op if
			// there's nothing active.
			activeTransitionRef.current?.skipTransition();

			const transition = document.startViewTransition(() => {
				flushSync(update);
			});
			activeTransitionRef.current = transition;

			// `.ready`/`.finished` rejecting is the normal outcome of an
			// interrupted transition (a skip, or a new one starting before this
			// one settles), not an error -- swallow rather than let either
			// become an unhandled rejection topic 16's error tracking would
			// otherwise pick up as a real bug.
			transition.ready.catch(() => {});
			transition.finished.catch(() => {}).finally(() => {
				if (activeTransitionRef.current === transition) {
					activeTransitionRef.current = null;
				}
			});
		},
		[prefersReducedMotion],
	);
}
